import { FC, useEffect } from 'react';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';
import { ensureEnum } from 'utils/tsHelpers';
import { ListUsageQueryContext } from './ListUsageQueryContext';
import { useListDependentsQuery } from '../listReferencesTable/hooks/useListDependentsQuery';
import {
  SORTABLE_TABLE_COLUMNS_TO_PROPERTIES,
  TABLE_COLUMNS,
} from '../listReferencesTable/constants';
import { useModifiersAndCreatorsQuery } from '../listReferencesTable/hooks/useModifiersAndCreatorsQuery';
import { useAngularListSegmentContext } from '../../contexts/AngularListSegmentContext';
import { ListReferenceSortableProperty } from '../../domain';

export const ListUsageQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { pageSize, currentPage } = usePaginationContext();
  const { sortBy, sortDirection } = useSortingContext();
  const { listDetails } = useAngularListSegmentContext();
  const { modifiersAndCreatorsById } = useModifiersAndCreatorsQuery();
  const [fetchListDependents, { data, loading, networkStatus, error }] = useListDependentsQuery();
  const sortedTableColumnId = ensureEnum(sortBy, TABLE_COLUMNS, TABLE_COLUMNS.NAME);
  const sortedProperty = ensureEnum(
    SORTABLE_TABLE_COLUMNS_TO_PROPERTIES[sortedTableColumnId],
    ListReferenceSortableProperty,
    ListReferenceSortableProperty.name,
  );

  useEffect(() => {
    if (listDetails?.emailListId !== undefined) {
      fetchListDependents({
        variables: {
          filterInfo: { listId: listDetails.emailListId },
          pagination: { limit: pageSize, offset: (currentPage - 1) * pageSize },
          sort: {
            sortBy: sortedProperty,
            sortDirection,
          },
        },
      });
    }
  }, [listDetails?.emailListId, pageSize, currentPage, sortedProperty, sortDirection]);

  const isLoading = networkStatus < 7 || loading;

  const context = {
    isLoading,
    isError: !!error,
    listReferences: data?.findListUsage.results ?? [],
    totalCount: data?.findListUsage.info.count ?? 0,
    modifiersAndCreatorsById,
  };

  return (
    <ListUsageQueryContext.Provider value={context}>{children}</ListUsageQueryContext.Provider>
  );
};
